import { useEffect } from 'react';

interface LocalSEOProps {
  city?: string;
  region?: string;
  services?: string[];
}

export const LocalSEO = ({ 
  city = "UK", 
  region = "United Kingdom",
  services = ["plumber", "electrician", "heating engineer", "carpenter", "painter", "builder"]
}: LocalSEOProps) => {
  
  useEffect(() => {
    // Add city-specific structured data for LocalBusiness
    const localBusinessData = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": `Sortable - Compare Local Tradespeople in ${city}`,
      "description": `Compare quotes from verified local tradespeople in ${city}. Get up to 5 free quotes from ${services.join(', ')} and more in your area.`,
      "url": `https://sortable.uk/?location=${encodeURIComponent(city)}`,
      "areaServed": {
        "@type": "City",
        "name": city,
        "containedInPlace": {
          "@type": "Country", 
          "name": region
        }
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": `Trade Services in ${city}`,
        "itemListElement": services.map(service => ({
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": `${service.charAt(0).toUpperCase() + service.slice(1)} Services in ${city}`,
            "description": `Professional ${service} services available 24/7 in ${city} with fast response guarantee`,
            "areaServed": {
              "@type": "City",
              "name": city
            }
          }
        }))
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "2847",
        "bestRating": "5"
      }
    };

    // Add service-specific structured data
    const servicesData = services.map(service => ({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": `${service.charAt(0).toUpperCase() + service.slice(1)} in ${city}`,
      "description": `Professional ${service} services in ${city}. Compare quotes from verified, insured professionals.`,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Sortable"
      },
      "areaServed": {
        "@type": "City",
        "name": city
      },
      "availableChannel": {
        "@type": "ServiceChannel",
        "serviceUrl": `https://sortable.uk/create-job?service=${service}&location=${encodeURIComponent(city)}`,
        "servicePhone": "+44-800-123-4567"
      }
    }));

    // Create script elements for structured data
    const scriptId = `local-seo-${city.toLowerCase().replace(/\s+/g, '-')}`;
    
    // Remove existing script if it exists
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.textContent = JSON.stringify([localBusinessData, ...servicesData]);
    document.head.appendChild(script);

    // Add meta keywords for the city
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      const cityKeywords = services.map(service => 
        `${service} ${city}, compare ${service} ${city}, local ${service} quotes ${city}`
      ).join(', ');
      
      const existingKeywords = metaKeywords.getAttribute('content') || '';
      metaKeywords.setAttribute('content', `${existingKeywords}, ${cityKeywords}`);
    }

    // Cleanup function
    return () => {
      const script = document.getElementById(scriptId);
      if (script) {
        script.remove();
      }
    };
  }, [city, region, services]);

  return null; // This component only adds structured data
};